<script setup lang="ts">
  import { IonContent, IonPage, IonCol, IonRow } from '@ionic/vue';
  import LargeNavbar from '@/components/LargeNavbar.vue';
  import { useI18n } from 'vue-i18n';
  import router from '@/router';
  import { useProfile } from '@/composables/useProfile';
  import DocumentWrapper from '@/components/shared/DocumentWrapper.vue';
  import { computed, onMounted } from 'vue';
  import { CoreStore, useCoreStore } from '@/stores/core-store';
  import { i18n } from '@/plugins/i18n';
  import { useBooking } from '@/composables/useBooking';
  import { storeToRefs } from 'pinia';
  import { useBookingStore } from '@/stores/booking-store';
  import { IDocument } from '@/interfaces/profile/profile-document.interface';
  import { DOCUMENT_VERIFICATION_STATUSES } from '@/utils/constants/general-constants';
  import { useRoute } from 'vue-router';

  const { t } = useI18n();
  const { loading, handleGetDocuments, documents } = useProfile();
  const { handleGetReservationContract } = useBooking();
  const route = useRoute();

  const coreStore: CoreStore = useCoreStore();
  const { contractData } = storeToRefs(useBookingStore());

  const backButton = () => {
    router.back();
  };

  const initialConfig = async () => {
    try {
      loading.value = true;
      documents.value = [];
      contractData.value = undefined;
      await handleGetDocuments();

      if (route.params.bookingId) {
        await handleGetReservationContract(route.params.bookingId as string);
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      loading.value = false;
    }
  };

  const documentsJoined = computed(() => {
    if (contractData?.value && contractData?.value?.status != 'empty') {
      const newDocument: IDocument = {
        id: -1,
        name: i18n.global.t('profile.document.rentalContract'),
        type: contractData?.value?.type ?? null,
        description: '',
        duration: 0,
        icon: 'document-outline',
        status: contractData?.value?.status ?? DOCUMENT_VERIFICATION_STATUSES.PENDING,
        isActive: true,
        period: '',
        createdAt: contractData.value.createdAt,
        updatedAt: contractData.value.updatedAt,
        countries: [],
        reasonForRejection: contractData?.value.reasonForRejection ?? null,
        Inputs: [
          {
            id: -1,
            label: i18n.global.t('profile.document.rentalContract'),
            placeholder: '',
            type: 'contract',
            fileType: null,
            min: 0,
            max: 0,
            Answers: [
              {
                DocumentsMasterInputId: -1,
                value: contractData?.value?.documentToSignUrl as string,
              },
            ],
          },
        ],
      };
      return [newDocument, ...documents.value];
    } else {
      return documents.value;
    }
  });

  onMounted(() => {
    initialConfig();
  });
</script>

<template>
  <ion-page @click="coreStore.setShowDropdown({ status: false, id: '' })">
    <ion-content>
      <LargeNavbar
        @backButton="backButton"
        show-back-button
        :title="t('profile.myDocumentTitle')"
        :show-actions="false"
      />
      <div class="mt-32 d-flex m-auto documents-wrapper">
        <ion-row class="w-100">
          <ion-col
            size="12"
            size-xl="6"
            class="mb-16"
            v-for="document in documentsJoined"
            :key="document.id"
          >
            <DocumentWrapper :document="document" :booking-id="route.params.bookingId as string" />
          </ion-col>
        </ion-row>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped lang="scss">
  .upload-wrapper {
    height: 170px;
  }

  ion-button {
    height: 36px;
  }

  ion-modal {
    --border-radius: 25px;
    --background: var(--ion-color-body);
  }

  ion-content {
    --border-radius: 25px;
    --background: white;
  }

  .documents-wrapper {
    width: 100%;
  }

  @media screen and (min-width: 768px) {
    .documents-wrapper {
      width: 75%;
    }
  }

  @media screen and (min-width: 992px) {
    .documents-wrapper {
      width: 50%;
    }
  }

  @media screen and (min-width: 1200px) {
    .documents-wrapper {
      width: 50%;
    }
  }

  @media screen and (min-width: 1490px) {
    .documents-wrapper {
      width: 50%;
    }
  }
</style>
